// Small building blocks shared by the Word CV builders. These encapsulate the
// docx boilerplate (borderless layout tables, the "TITLE ———" section header,
// divider rules, the page-number footer) so the per-section code reads close to
// the corresponding @react-pdf/renderer components.

import {
    AlignmentType,
    BorderStyle,
    Footer,
    HeightRule,
    LineRuleType,
    PageNumber,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
} from "docx";
import { A4, ptToHalfPoints, pxToTwips } from "./wordUnits";

// A minimal-height empty paragraph. Word merges two tables that sit directly
// next to each other (and flags a table that ends a cell), so we insert one of
// these between adjacent tables and after a table inside a cell.
export function tableGap() {
    return new Paragraph( { spacing: { before: 0, after: 0, line: 20, lineRule: LineRuleType.EXACT } } );
}

// Insert a tableGap() between any two consecutive Table blocks so Word does not
// merge them into a single table.
export function separateTables( elements ) {
    const out = [];
    elements.forEach( ( element, index ) => {
        if ( index > 0 && elements[ index - 1 ] instanceof Table && element instanceof Table ) {
            out.push( tableGap() );
        }
        out.push( element );
    } );
    return out;
}

// Usable text width = A4 width minus the 40px left/right page margins.
export const CONTENT_WIDTH = A4.width - pxToTwips( 40 ) * 2;

export function frac( fraction ) {
    return Math.round( CONTENT_WIDTH * fraction );
}

export const NO_CELL_BORDERS = {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
};

export const NO_TABLE_BORDERS = {
    top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
    insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
};

// A single TextRun. `pt` is a point size (converted to half-points for docx).
export function run( text, { pt, bold, italics, color, font, allCaps, underline } = {} ) {
    return new TextRun( {
        text: text ?? "",
        bold,
        italics,
        allCaps,
        ...( color ? { color } : {} ),
        ...( font ? { font } : {} ),
        ...( pt ? { size: ptToHalfPoints( pt ) } : {} ),
        ...( underline ? { underline: {} } : {} ),
    } );
}

export function para( children, options = {} ) {
    return new Paragraph( {
        children: Array.isArray( children ) ? children : [ children ],
        ...options,
    } );
}

// Borderless table cell.
export function cell( children, { widthTwips, columnSpan, valign, margins } = {} ) {
    return new TableCell( {
        children: Array.isArray( children ) ? children : [ children ],
        borders: NO_CELL_BORDERS,
        ...( widthTwips ? { width: { size: widthTwips, type: WidthType.DXA } } : {} ),
        ...( columnSpan ? { columnSpan } : {} ),
        ...( valign ? { verticalAlign: valign } : {} ),
        ...( margins ? { margins } : {} ),
    } );
}

// Borderless layout table. Defaults to the full content width; pass `width`
// (twips) for nested tables that must fit inside a parent cell.
export function layoutTable( rows, { columnWidths, width } = {} ) {
    return new Table( {
        width: { size: width ?? CONTENT_WIDTH, type: WidthType.DXA },
        borders: NO_TABLE_BORDERS,
        ...( columnWidths ? { columnWidths } : {} ),
        rows,
    } );
}

// Generic two-column layout row (default 30% / 70%), used heavily by the CSSF
// variant. `rightMarginLeftPx` mirrors the PDF's marginLeft between columns.
export function twoColumnRow( leftChildren, rightChildren, { leftFrac = 0.30, leftValign, rightValign, rightMarginLeftPx } = {} ) {
    const leftW = frac( leftFrac );
    const rightW = CONTENT_WIDTH - leftW;
    return layoutTable(
        [
            new TableRow( {
                children: [
                    cell( leftChildren, { widthTwips: leftW, valign: leftValign } ),
                    cell( rightChildren, {
                        widthTwips: rightW,
                        valign: rightValign,
                        margins: rightMarginLeftPx
                            ? { left: pxToTwips( rightMarginLeftPx ), top: 0, bottom: 0, right: 0 }
                            : undefined,
                    } ),
                ],
            } ),
        ],
        { columnWidths: [ leftW, rightW ] }
    );
}

// "SECTION TITLE ————————" : title in the accent color followed by a rule that
// fills the remaining width, vertically centered on the title (a 2-cell table).
export function sectionTitle( text, accent, { font, pt = 12 } = {} ) {
    const titleWidth = Math.min(
        frac( 0.6 ),
        Math.max( frac( 0.12 ), pxToTwips( ( text || "" ).length * 7.5 ) )
    );
    const ruleWidth = CONTENT_WIDTH - titleWidth;

    return new Table( {
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        borders: NO_TABLE_BORDERS,
        columnWidths: [ titleWidth, ruleWidth ],
        rows: [
            new TableRow( {
                children: [
                    new TableCell( {
                        borders: NO_CELL_BORDERS,
                        width: { size: titleWidth, type: WidthType.DXA },
                        margins: { top: 0, bottom: 0, left: 0, right: 0 },
                        children: [
                            para( run( text, { pt, bold: true, color: accent, font, allCaps: true } ),
                                { spacing: { after: 0 } } ),
                        ],
                    } ),
                    new TableCell( {
                        width: { size: ruleWidth, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        borders: {
                            ...NO_CELL_BORDERS,
                            bottom: { style: BorderStyle.SINGLE, size: 8, color: accent },
                        },
                        margins: { top: 0, bottom: 0, left: pxToTwips( 5 ), right: 0 },
                        children: [ para( [], { spacing: { after: 0 } } ) ],
                    } ),
                ],
            } ),
        ],
    } );
}

// Full-width horizontal rule used between entries.
export function dividerRule( accent, { sizeEighths = 8, spaceBefore = 30, spaceAfter = 30 } = {} ) {
    return para( [], {
        spacing: { before: spaceBefore, after: spaceAfter },
        border: { bottom: { style: BorderStyle.SINGLE, size: sizeEighths, color: accent, space: 1 } },
    } );
}

// Bullet line ("• text"), using Word's native bullet list.
export function bulletLine( text, { pt = 8, font, color } = {} ) {
    return para( run( text, { pt, font, color } ), {
        bullet: { level: 0 },
        spacing: { after: 0 },
    } );
}

// CSSF-style section title: a right-aligned label in the left column and a thick
// solid accent bar filling the right column (2-cell table). Supports a multi-line
// label via "\n".
export function barSectionTitle( label, { accent, font, labelFrac = 0.30, labelPt = 9, barColor, barHeightPx = 5 } = {} ) {
    const labelW = frac( labelFrac );
    const restW = CONTENT_WIDTH - labelW;
    const fill = barColor ?? accent;

    const labelRuns = String( label ).split( "\n" ).map( ( line, i ) =>
        new TextRun( {
            text: line,
            ...( i > 0 ? { break: 1 } : {} ),
            color: accent,
            size: ptToHalfPoints( labelPt ),
            ...( font ? { font } : {} ),
        } )
    );

    const bar = new Table( {
        width: { size: restW, type: WidthType.DXA },
        columnWidths: [ restW ],
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow( {
                height: { value: pxToTwips( barHeightPx ), rule: HeightRule.EXACT },
                children: [
                    new TableCell( {
                        width: { size: restW, type: WidthType.DXA },
                        borders: NO_CELL_BORDERS,
                        shading: { fill },
                        children: [ para( [], { spacing: { after: 0 } } ) ],
                    } ),
                ],
            } ),
        ],
    } );

    return new Table( {
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        columnWidths: [ labelW, restW ],
        borders: NO_TABLE_BORDERS,
        rows: [
            new TableRow( {
                children: [
                    new TableCell( {
                        width: { size: labelW, type: WidthType.DXA },
                        borders: NO_CELL_BORDERS,
                        verticalAlign: VerticalAlign.CENTER,
                        children: [ para( labelRuns, { alignment: AlignmentType.RIGHT, spacing: { after: 0 } } ) ],
                    } ),
                    new TableCell( {
                        width: { size: restW, type: WidthType.DXA },
                        borders: NO_CELL_BORDERS,
                        verticalAlign: VerticalAlign.CENTER,
                        margins: { top: pxToTwips( 4 ), bottom: pxToTwips( 4 ), left: 0, right: 0 },
                        // A cell must not end with a table, so close it with a gap paragraph.
                        children: [ bar, tableGap() ],
                    } ),
                ],
            } ),
        ],
    } );
}

// Europass-style section title: "• LABEL" (bold, uppercase) followed by a grey
// rule. Returns [titleParagraph, ruleParagraph]; pass `indent` to inset both.
export function bulletSectionTitle( label, { font, ruleColor = "979797", indent } = {} ) {
    const title = para(
        [ run( "• ", { pt: 11, font } ), run( label, { pt: 9, bold: true, allCaps: true, font } ) ],
        { spacing: { after: 0 }, ...( indent ? { indent } : {} ) }
    );
    const rule = para( [], {
        border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: ruleColor } },
        spacing: { before: pxToTwips( 1 ), after: pxToTwips( 5 ) },
        ...( indent ? { indent } : {} ),
    } );
    return [ title, rule ];
}

// "- text" bullet (CSSF uses a dash, not a real bullet), with a hanging indent so
// wrapped lines align under the text.
export function dashBullet( text, { pt = 9, font, color, char = "-", indentPx = 12 } = {} ) {
    return para(
        [ run( `${ char } `, { pt, font, color } ), run( text, { pt, font, color } ) ],
        { indent: { left: pxToTwips( indentPx ), hanging: pxToTwips( indentPx ) }, spacing: { after: 0 } }
    );
}

// Page-number footer "{n} / {total}", right-aligned, matching the PDF.
export function pageNumberFooter( { font } = {} ) {
    return new Footer( {
        children: [
            new Paragraph( {
                alignment: AlignmentType.RIGHT,
                children: [
                    new TextRun( {
                        children: [ PageNumber.CURRENT, " / ", PageNumber.TOTAL_PAGES ],
                        size: ptToHalfPoints( 8 ),
                        color: "000000",
                        ...( font ? { font } : {} ),
                    } ),
                ],
            } ),
        ],
    } );
}
